import React, {useEffect, useMemo, useState} from "react"
import {createPortal} from "react-dom"
// MUI
import {Box, Button, IconButton, Typography} from "@mui/material"
import {CloseRounded, ChevronLeftRounded, ChevronRightRounded} from "@mui/icons-material"
// Translation
import {useTranslation} from "react-i18next"
// Data
import {useModule} from "@/context/ModuleProvider"
// Misc
import {packagingTokens as tk} from "@/theme/packagingTokens"
import {PackagingSection} from "./types"

// ── Packaging tour v2 (2026-07 rework, claude/blueprints/packaging-tour-*) ──
// Numbered dots pinned to the real UI elements, one short pill of information
// per step, and a Back / Next controller attached under the active pill.
// Walking the steps auto-navigates between sections. No dimming backdrop —
// the tour annotates the live app instead of covering it.

type TourStep = {
    n: number
    section: PackagingSection
    selector: string // querySelector for the annotated element
    appsOnly?: boolean
}

// Step numbering follows the blueprint: 1 sidebar → 2-4 brands/batches →
// 5-8 products/batches → 9-10 dashboard → 11 knowledge base.
const STEPS: TourStep[] = [
    {n: 1, section: "dashboard", selector: "#pkg-nav-brands"},
    {n: 2, section: "brands", selector: '[data-step-target="create-brand"]'},
    {n: 3, section: "brands", selector: '[data-step-target="create-range"]'},
    {n: 4, section: "batches", selector: '[data-step-target="batch-export"]'},
    {n: 5, section: "products", selector: "#pkg-nav-products"},
    {n: 6, section: "products", selector: '[data-step-target="create-product-gs1"]'},
    {n: 7, section: "batches", selector: '[data-step-target="batch-import"]'},
    {n: 8, section: "products", selector: '[data-step-target="filter-gtin"]'},
    {n: 9, section: "dashboard", selector: '[data-step-target="dashboard-stats"]'},
    {n: 10, section: "dashboard", selector: "#pkg-nav-health"},
    {n: 11, section: "dashboard", selector: '[data-step-target="knowledge-base"]'},
]

const DOT = 26 // numbered dot diameter

type SidebarTourProps = {
    open: boolean
    onClose: () => void
    // The tour drives the section: steps live on different pages.
    section: PackagingSection
    onSectionChange: (section: PackagingSection) => void
}

export default function SidebarTour({open, onClose, section, onSectionChange}: SidebarTourProps) {

    const {t} = useTranslation(["common"])
    const {modules} = useModule()
    const [index, setIndex] = useState(0)
    // Positions of every step of the CURRENT section (n -> rect), so all the
    // page's dots show at once like the blueprint; the active one carries the
    // pill + controller.
    const [rects, setRects] = useState<Record<number, DOMRect>>({})

    const steps = useMemo(
        () => STEPS.filter((s) => !s.appsOnly || modules?.geoloc),
        [modules],
    )
    const step = steps[index]

    // Restart from the beginning each time the tour opens.
    useEffect(() => {
        if (open) setIndex(0)
    }, [open])

    // Keep the active step's section in front of the user.
    useEffect(() => {
        if (!open || !step) return
        if (step.section !== section) onSectionChange(step.section)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, step])

    // Measure all same-section dots continuously (sections mount async and
    // shift while data loads — same strategy as StepSpotlight).
    useEffect(() => {
        if (!open || !step) {setRects({}); return}
        const measure = () => {
            const next: Record<number, DOMRect> = {}
            steps.forEach((s) => {
                if (s.section !== step.section) return
                const el = document.querySelector(s.selector)
                if (el) next[s.n] = el.getBoundingClientRect()
            })
            setRects((prev) => {
                const keys = Object.keys(next)
                const same =
                    keys.length === Object.keys(prev).length &&
                    keys.every((k) => {
                        const a = prev[Number(k)], b = next[Number(k)]
                        return a && Math.abs(a.top - b.top) < 1 && Math.abs(a.left - b.left) < 1 && Math.abs(a.width - b.width) < 1
                    })
                return same ? prev : next
            })
        }
        measure()
        const interval = setInterval(measure, 300)
        window.addEventListener("resize", measure)
        window.addEventListener("scroll", measure, true)
        return () => {
            clearInterval(interval)
            window.removeEventListener("resize", measure)
            window.removeEventListener("scroll", measure, true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, step, steps])

    if (!open || !step) return null

    const isLast = index === steps.length - 1
    const vw = document.body.clientWidth || document.documentElement.clientWidth

    // A dot sits on the element's top-right corner (blueprint), the active
    // pill extends beside it, and the controller hangs right under the pill.
    const dotPos = (r: DOMRect) => ({
        top: r.top + window.scrollY - DOT / 3,
        left: Math.min(r.right + window.scrollX - DOT / 2, vw - DOT - 8),
    })

    const activeRect = rects[step.n]
    const active = activeRect ? dotPos(activeRect) : {top: 120 + window.scrollY, left: vw / 2}
    // Pill under the dot, clamped inside the viewport.
    const PILL_W = 320
    const pillLeft = Math.max(8, Math.min(active.left + DOT + 6, vw - PILL_W - 16))
    // Hanging the pill under the dot puts its white body across the bottom
    // and right edge of button-sized targets (the dot is anchored at the
    // target's TOP corner) — which reads as a broken white border around
    // the button. Clear small targets entirely; large ones (cards, whole
    // sections) keep the dot-relative annotation placement.
    const naivePillTop = active.top + DOT + 8
    const targetBottom = activeRect ? activeRect.bottom + window.scrollY : null
    const pillTop =
        targetBottom !== null && activeRect.height <= 60 && naivePillTop < targetBottom + 6
            ? targetBottom + 10
            : naivePillTop

    return createPortal(
        <Box sx={{position: "absolute", inset: 0, zIndex: 1400, pointerEvents: "none"}} data-testid={"tour-v2"}>
            {/* Passive numbered dots for the section's other steps */}
            {steps
                .filter((s) => s.section === step.section && s.n !== step.n && rects[s.n])
                .map((s) => {
                    const p = dotPos(rects[s.n])
                    return (
                        <Box
                            key={s.n}
                            onClick={() => setIndex(steps.findIndex((x) => x.n === s.n))}
                            sx={{
                                position: "absolute", top: p.top, left: p.left,
                                width: DOT, height: DOT, borderRadius: "50%",
                                backgroundColor: tk.color.green, color: "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "13px", fontWeight: 700,
                                border: "2px solid #fff", boxShadow: tk.shadow.md,
                                opacity: 0.55, cursor: "pointer", pointerEvents: "auto",
                                transition: "all .25s ease",
                                "&:hover": {opacity: 1},
                            }}
                        >
                            {s.n}
                        </Box>
                    )
                })}

            {/* Active dot */}
            <Box
                sx={{
                    position: "absolute", top: active.top, left: active.left,
                    width: DOT, height: DOT, borderRadius: "50%",
                    backgroundColor: tk.color.green, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "13px", fontWeight: 700,
                    border: "2px solid #fff", boxShadow: tk.shadow.lg,
                    transition: "all .25s ease",
                }}
            >
                {step.n}
            </Box>

            {/* Pill + controller (the interactive part) */}
            <Box sx={{position: "absolute", top: pillTop, left: pillLeft, width: PILL_W, pointerEvents: "auto", transition: "all .25s ease"}}>
                <Box
                    sx={{
                        display: "inline-flex", alignItems: "center", gap: 1,
                        px: 1.5, py: 0.75,
                        backgroundColor: tk.color.surface,
                        border: `2px solid ${tk.color.green}`,
                        borderRadius: tk.radius.pill,
                        boxShadow: tk.shadow.lg,
                    }}
                >
                    <Box
                        sx={{
                            width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                            backgroundColor: tk.color.green, color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "11.5px", fontWeight: 700,
                        }}
                    >
                        {step.n}
                    </Box>
                    <Typography sx={{fontSize: "13.5px", fontWeight: 600, color: tk.color.textPrimary}}>
                        {t(`tour2_step_${step.n}`)}
                    </Typography>
                </Box>

                {/* Navigation bar */}
                <Box
                    sx={{
                        mt: 1, display: "inline-flex", alignItems: "center", gap: 0.5,
                        px: 1, py: 0.5,
                        backgroundColor: tk.color.surface,
                        border: `1px solid ${tk.color.border}`,
                        borderRadius: tk.radius.pill,
                        boxShadow: tk.shadow.md,
                    }}
                >
                    <Button
                        size={"small"} disabled={index === 0}
                        onClick={() => setIndex(index - 1)}
                        startIcon={<ChevronLeftRounded sx={{fontSize: 16}} />}
                        sx={{textTransform: "none", fontSize: "12.5px", fontWeight: 600, minWidth: 0, color: tk.color.textSecondary}}
                    >
                        {t("tour2_back")}
                    </Button>
                    <Typography sx={{fontSize: "12px", color: tk.color.textTertiary, px: 0.5}}>
                        {index + 1} {t("tour2_of")} {steps.length}
                    </Typography>
                    <Button
                        size={"small"}
                        onClick={() => (isLast ? onClose() : setIndex(index + 1))}
                        endIcon={!isLast ? <ChevronRightRounded sx={{fontSize: 16}} /> : undefined}
                        sx={{textTransform: "none", fontSize: "12.5px", fontWeight: 700, minWidth: 0, color: tk.color.green}}
                    >
                        {isLast ? t("tour2_finish") : t("tour2_next")}
                    </Button>
                    <IconButton size={"small"} onClick={onClose} aria-label={t("close")} sx={{color: tk.color.textTertiary}}>
                        <CloseRounded sx={{fontSize: 15}} />
                    </IconButton>
                </Box>
            </Box>
        </Box>,
        document.body,
    )
}
